import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import ElectricalServicesRoundedIcon from "@mui/icons-material/ElectricalServicesRounded";
import FactCheckRoundedIcon from "@mui/icons-material/FactCheckRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import { useStyle } from "../../context/styleContext/styleContext";

const API_BASE =
  process.env.NODE_ENV === "development"
    ? "http://localhost:8080/api/marketing"
    : "https://api.parishaudio.com/api/marketing";

export default function MarketingEmailTesting() {
  const { style } = useStyle();

  const [testEmail, setTestEmail] = useState("nate.j.parish@gmail.com");
  const [limit, setLimit] = useState("");
  const [delayMs, setDelayMs] = useState("250");
  const [confirmFinalSend, setConfirmFinalSend] = useState(false);

  const [status, setStatus] = useState({ type: "", message: "" });
  const [loadingKey, setLoadingKey] = useState("");
  const [lastResults, setLastResults] = useState(null);

  const commonFieldSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        color: style.color4,
        backgroundColor: "rgba(255,255,255,0.03)",
        borderRadius: "8px",
      },
      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: style.color2,
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: style.color3,
      },
      "& .Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: style.accentGreen,
      },
      "& .MuiInputBase-input::placeholder": {
        color: style.color3,
        opacity: 1,
      },
      "& .MuiInputLabel-root": {
        color: style.color3,
      },
      "& .MuiInputLabel-root.Mui-focused": {
        color: style.color4,
      },
      "& .MuiFormHelperText-root": {
        color: style.color3,
      },
    }),
    [style]
  );

  const sendRequest = async ({ key, endpoint, method = "POST", body = null }) => {
    setLoadingKey(key);
    setStatus({ type: "", message: "" });
    setLastResults(null);

    try {
      const response = await fetch(endpoint, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          data?.message ||
            data?.error ||
            `Request failed with status ${response.status}`
        );
      }

      setLastResults(data?.results || null);

      setStatus({
        type: "success",
        message: data?.message || "Request completed successfully.",
      });

      return data;
    } catch (error) {
      setStatus({
        type: "error",
        message: error.message || "Request failed.",
      });

      return null;
    } finally {
      setLoadingKey("");
    }
  };

  const testConnection = () => {
    return sendRequest({
      key: "connection",
      endpoint: `${API_BASE}/test-connection`,
      method: "GET",
    });
  };

  const sendTestEmail = () => {
    if (!testEmail.trim()) {
      setStatus({
        type: "error",
        message: "Enter your test email address first.",
      });
      return;
    }

    return sendRequest({
      key: "test",
      endpoint: `${API_BASE}/peakline/test`,
      method: "POST",
      body: {
        email: testEmail.trim().toLowerCase(),
        firstName: "Nate",
      },
    });
  };

  const dryRunCampaign = () => {
    return sendRequest({
      key: "dry-run",
      endpoint: `${API_BASE}/peakline/send`,
      method: "POST",
      body: {
        confirmSend: true,
        dryRun: true,
        limit: limit ? Number(limit) : 0,
        delayMs: delayMs ? Number(delayMs) : 250,
      },
    });
  };

  const sendLimitedCampaign = () => {
    if (!limit || Number(limit) <= 0) {
      setStatus({
        type: "error",
        message: "Enter a limit greater than 0 before sending a limited test batch.",
      });
      return;
    }

    return sendRequest({
      key: "limited",
      endpoint: `${API_BASE}/peakline/send`,
      method: "POST",
      body: {
        confirmSend: true,
        dryRun: false,
        limit: Number(limit),
        delayMs: delayMs ? Number(delayMs) : 250,
      },
    });
  };

  const sendFinalCampaign = () => {
    if (!confirmFinalSend) {
      setStatus({
        type: "error",
        message:
          "Check the final confirmation box before sending to the full mailing list.",
      });
      return;
    }

    const typedConfirmation = window.prompt(
      'This will send the launch email to everyone with okToEmail: true. Type "SEND" to continue.'
    );

    if (typedConfirmation !== "SEND") {
      setStatus({
        type: "error",
        message: "Final send canceled. Confirmation text did not match.",
      });
      return;
    }

    return sendRequest({
      key: "final",
      endpoint: `${API_BASE}/peakline/send`,
      method: "POST",
      body: {
        confirmSend: true,
        dryRun: false,
        limit: 0,
        delayMs: delayMs ? Number(delayMs) : 250,
      },
    });
  };

  const renderLoadingIcon = (key) =>
    loadingKey === key ? (
      <CircularProgress size={16} sx={{ color: style.color4 }} />
    ) : (
      <SendRoundedIcon />
    );

  const ActionCard = ({
    icon,
    title,
    description,
    endpoint,
    buttonLabel,
    buttonColor = style.accentGreen,
    loadingName,
    onClick,
    disabled = false,
  }) => (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: style.darkPanel,
        borderRadius: "16px",
        p: { xs: 1.15, md: 1.3 },
        border: "1px solid rgba(255,255,255,0.08)",
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={1.25}
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
      >
        <Stack direction="row" spacing={1.2} alignItems="flex-start">
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: "10px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255,255,255,0.05)",
              border: "1px solid rgba(255,255,255,0.06)",
              flexShrink: 0,
            }}
          >
            {icon}
          </Box>

          <Box>
            <Typography
              sx={{
                color: style.color4,
                fontWeight: 800,
                fontSize: "1.02rem",
                lineHeight: 1.2,
                mb: 0.4,
              }}
            >
              {title}
            </Typography>

            <Typography
              sx={{
                color: style.color3,
                fontSize: "0.92rem",
                lineHeight: 1.6,
                maxWidth: "760px",
              }}
            >
              {description}
            </Typography>

            <Typography
              sx={{
                color: style.color3,
                fontSize: "0.78rem",
                lineHeight: 1.5,
                mt: 0.6,
                wordBreak: "break-all",
              }}
            >
              Endpoint: {endpoint}
            </Typography>
          </Box>
        </Stack>

        <Button
          variant="contained"
          onClick={onClick}
          disabled={disabled || !!loadingKey}
          startIcon={renderLoadingIcon(loadingName)}
          sx={{
            borderRadius: "5px",
            textTransform: "none",
            fontWeight: 800,
            color: style.color4,
            backgroundColor: buttonColor,
            minWidth: { xs: "100%", md: 230 },
            minHeight: 46,
            alignSelf: { xs: "stretch", md: "center" },
            "&:hover": {
              backgroundColor: buttonColor,
              filter: "brightness(1.05)",
            },
            "&.Mui-disabled": {
              backgroundColor: "rgba(255,255,255,0.12)",
              color: style.color3,
            },
          }}
        >
          {loadingKey === loadingName ? "Working..." : buttonLabel}
        </Button>
      </Stack>
    </Paper>
  );

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "100vw",
        overflowX: "hidden",
        backgroundColor: "black",
        px: { xs: 1.25, sm: 1.5, md: 2.5 },
        py: { xs: 1.25, md: 2 },
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: "1120px",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        <Stack spacing={2}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                px: { xs: 1.25, sm: 1.5, md: 2 },
                pt: { xs: 1.25, md: 1.5 },
                pb: { xs: 1, md: 1.25 },
                backgroundColor: style.darkPanel,
                borderBottom: "1px solid rgba(255,255,255,0.08)",
              }}
            >
              <Stack spacing={1.1}>
                <Chip
                  icon={<CampaignRoundedIcon />}
                  label="Admin Marketing Tools"
                  sx={{
                    alignSelf: "flex-start",
                    borderRadius: "5px",
                    fontWeight: 700,
                    color: style.color4,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    height: 28,
                  }}
                />

                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "1.75rem", md: "2.3rem" },
                    fontWeight: 800,
                    lineHeight: 1.05,
                    letterSpacing: "-0.03em",
                    color: style.color4,
                  }}
                >
                  Marketing Email Dashboard
                </Typography>

                <Typography
                  variant="body1"
                  sx={{
                    color: style.color3,
                    maxWidth: "780px",
                    fontSize: { xs: "0.95rem", md: "1rem" },
                    lineHeight: 1.65,
                  }}
                >
                  Test the marketing email, confirm the mailing list count, send
                  a limited batch, and then send the final campaign to everyone
                  with <strong>okToEmail: true</strong>.
                </Typography>
              </Stack>
            </Box>

            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Paper
                elevation={0}
                sx={{
                  backgroundColor: style.darkPanel,
                  borderRadius: "14px",
                  p: { xs: 1.1, md: 1.3 },
                  border: "1px solid rgba(255,255,255,0.08)",
                }}
              >
                <Stack spacing={1.5}>
                  <TextField
                    fullWidth
                    label="Test Email Address"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="nate.j.parish@gmail.com"
                    sx={commonFieldSx}
                    helperText="The backend only allows the configured MARKETING_TEST_EMAIL for test sends."
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                    <TextField
                      fullWidth
                      label="Limited Batch Size"
                      value={limit}
                      onChange={(e) => setLimit(e.target.value)}
                      placeholder="3"
                      type="number"
                      sx={commonFieldSx}
                      helperText="Use for a small live batch before sending to everyone."
                    />

                    <TextField
                      fullWidth
                      label="Delay Between Emails (ms)"
                      value={delayMs}
                      onChange={(e) => setDelayMs(e.target.value)}
                      placeholder="250"
                      type="number"
                      sx={commonFieldSx}
                      helperText="250ms is safe for a small list."
                    />
                  </Stack>

                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={confirmFinalSend}
                        onChange={(e) => setConfirmFinalSend(e.target.checked)}
                        sx={{
                          color: style.color3,
                          "&.Mui-checked": {
                            color: style.accentGreen,
                          },
                        }}
                      />
                    }
                    label="I understand that the final send will email everyone currently subscribed to Parish Audio marketing emails."
                    sx={{
                      color: style.color4,
                      alignItems: "flex-start",
                      "& .MuiFormControlLabel-label": {
                        lineHeight: 1.5,
                        fontSize: "0.95rem",
                      },
                    }}
                  />
                </Stack>
              </Paper>
            </Box>
          </Paper>

          {status.message ? (
            <Alert
              severity={status.type === "error" ? "error" : "success"}
              sx={{
                borderRadius: "12px",
                backgroundColor:
                  status.type === "error"
                    ? "rgba(140, 30, 30, 0.25)"
                    : "rgba(38, 166, 104, 0.18)",
                color: style.color4,
                border: "1px solid rgba(255,255,255,0.08)",
                "& .MuiAlert-icon": {
                  color: style.color4,
                },
              }}
            >
              {status.message}
            </Alert>
          ) : null}

          {lastResults ? (
            <Paper
              elevation={0}
              sx={{
                borderRadius: "16px",
                backgroundColor: style.color1,
                border: `1px solid ${style.color2}`,
                p: { xs: 1.25, md: 1.5 },
              }}
            >
              <Stack spacing={1.25}>
                <Typography
                  sx={{
                    color: style.color4,
                    fontWeight: 800,
                    fontSize: "1.1rem",
                  }}
                >
                  Last Campaign Results
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  flexWrap="wrap"
                >
                  <Chip
                    label={`Found: ${lastResults.totalFound ?? 0}`}
                    sx={{
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Sent: ${lastResults.sent ?? 0}`}
                    sx={{
                      color: style.color4,
                      backgroundColor: "rgba(38,166,104,0.18)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Skipped: ${lastResults.skipped ?? 0}`}
                    sx={{
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.06)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={`Failed: ${lastResults.failed ?? 0}`}
                    sx={{
                      color: style.color4,
                      backgroundColor:
                        (lastResults.failed ?? 0) > 0
                          ? "rgba(140,30,30,0.25)"
                          : "rgba(255,255,255,0.06)",
                      fontWeight: 700,
                    }}
                  />
                  <Chip
                    label={lastResults.dryRun ? "Dry Run" : "Live Send"}
                    sx={{
                      color: style.color4,
                      backgroundColor: lastResults.dryRun
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(38,166,104,0.18)",
                      fontWeight: 700,
                    }}
                  />
                </Stack>

                {Array.isArray(lastResults.failures) &&
                lastResults.failures.length > 0 ? (
                  <Box>
                    <Typography
                      sx={{
                        color: style.color3,
                        fontSize: "0.9rem",
                        mb: 0.75,
                      }}
                    >
                      Failures:
                    </Typography>

                    <Box
                      component="pre"
                      sx={{
                        m: 0,
                        p: 1.25,
                        borderRadius: "10px",
                        backgroundColor: "rgba(0,0,0,0.25)",
                        color: style.color3,
                        fontSize: "0.78rem",
                        overflowX: "auto",
                      }}
                    >
                      {JSON.stringify(lastResults.failures, null, 2)}
                    </Box>
                  </Box>
                ) : null}
              </Stack>
            </Paper>
          ) : null}

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              minWidth: 0,
              borderRadius: "20px",
              overflow: "hidden",
              backgroundColor: style.color1,
              border: `1px solid ${style.color2}`,
              boxSizing: "border-box",
            }}
          >
            <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
              <Stack spacing={1.25}>
                <ActionCard
                  icon={<ElectricalServicesRoundedIcon sx={{ color: style.color4 }} />}
                  title="Test Marketing Email Connection"
                  description="Verifies the separate marketing email transporter can connect to SES."
                  endpoint={`${API_BASE}/test-connection`}
                  buttonLabel="Test Connection"
                  loadingName="connection"
                  onClick={testConnection}
                />

                <ActionCard
                  icon={<MailOutlineRoundedIcon sx={{ color: style.color4 }} />}
                  title="Send Test Email"
                  description="Sends the real launch email to your configured test email only."
                  endpoint={`${API_BASE}/peakline/test`}
                  buttonLabel="Send Test Email"
                  loadingName="test"
                  onClick={sendTestEmail}
                />

                <ActionCard
                  icon={<FactCheckRoundedIcon sx={{ color: style.color4 }} />}
                  title="Dry Run Campaign"
                  description="Counts everyone currently eligible for the campaign without sending any emails."
                  endpoint={`${API_BASE}/peakline/send`}
                  buttonLabel="Run Dry Run"
                  loadingName="dry-run"
                  onClick={dryRunCampaign}
                />

                <ActionCard
                  icon={<CampaignRoundedIcon sx={{ color: style.color4 }} />}
                  title="Send Limited Live Batch"
                  description="Sends the real campaign to the first limited number of subscribers. Use this with a small number like 3 before the final send."
                  endpoint={`${API_BASE}/peakline/send`}
                  buttonLabel="Send Limited Batch"
                  loadingName="limited"
                  onClick={sendLimitedCampaign}
                />

                <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

                <ActionCard
                  icon={<WarningAmberRoundedIcon sx={{ color: style.color4 }} />}
                  title="Final Send to Full Mailing List"
                  description='Sends the launch email to every user with okToEmail: true. Requires checkbox confirmation and typing "SEND" in a browser prompt.'
                  endpoint={`${API_BASE}/peakline/send`}
                  buttonLabel="Send Final Campaign"
                  buttonColor="#b45309"
                  loadingName="final"
                  onClick={sendFinalCampaign}
                  disabled={!confirmFinalSend}
                />
              </Stack>
            </Box>
          </Paper>
        </Stack>
      </Box>
    </Box>
  );
}